import { faWandMagicSparkles } from '@fortawesome/pro-regular-svg-icons'
import { Flex, Box, Text, TextInput, Select, Button } from '@mantine/core'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useQueryGetAllItems } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreUserAddress, type CoreBundle } from '@flecto/types'
import { RichTextEditor, Badge, Icon } from '@flecto/ui'
import { AlertActionable } from '@flecto/ui/@shared/AlertActionable'
import { useAddressInline } from '@flecto/utils'

import { useBundleGeneratorAI } from '../../hooks/useBundleGeneratorAI'
import { GridItemPhotosManager } from '../GridItemPhotosManager/GridItemPhotosManager'
import { useFormContext } from './FormBundle'

function AddressSelect({ address }: { address?: CoreUserAddress }) {
  const getAddressLabel = useAddressInline()
  const options = useMemo(
    () =>
      address
        ? [{ value: `${address.id}`, label: getAddressLabel(address, true) }]
        : [],
    [address, getAddressLabel]
  )

  return (
    <Select
      data={options}
      value={`${address?.id}`}
      allowDeselect={false}
      disabled
    />
  )
}

export function TabProperties() {
  const form = useFormContext()
  const { t } = useTranslation('saas', { keyPrefix: 'items' })
  const isGeneratingRef = useRef('')
  const previousValueRef = useRef<string | undefined>(form.values.description)
  const descriptionId =
    isGeneratingRef?.current === 'true' ? 'rich-text-editor-description' : ''
  const [oldDescription, setOldDescription] = useState<string | undefined>(
    form.values.description || ''
  )

  const bundleItemIds = form.values.bundle_items.map((item) => item.item_id)
  const { data: items } = useQueryGetAllItems()

  const allItems = items?.item || []
  const bundleItems = allItems.filter((item) => bundleItemIds.includes(item.id))
  const categories =
    bundleItems
      .map((item) => ({
        label: item.categories?.join(' > ') || '',
        value: `${item?.category_id}` || '',
      }))
      .filter((category) => category.label && category.value)
      .filter(
        (category, index, self) =>
          index ===
          self.findIndex(
            (c) => c.label === category.label && c.value === category.value
          )
      )
      .sort() || []

  const bundleAddress = bundleItems?.[0]?.owner_address

  const handleChangeDescription = useCallback(
    (text?: string) => {
      form.setFieldValue('description', text)
    },
    [form]
  )

  const {
    getDescriptionWithAI,
    generateListPhotosWithAI,
    cancelGenerateAI,
    isLoading: isLoadingAI,
  } = useBundleGeneratorAI()

  const handleGenerateContentWithAI = async () => {
    const items = form.values.bundle_items ?? []

    if (!items.length) {
      return
    }

    if (form.values.description && form.values.description.length > 0) {
      setOldDescription(form.values.description)
    } else {
      setOldDescription('')
    }

    form.setFieldValue('description', '')
    form.values.description = ''

    getDescriptionWithAI(items, (message) => {
      const content = `${form.values.description}${message}`
      form.setFieldValue('description', content)
      form.values.description = content
    })

    generateListPhotosWithAI(items)
  }

  const handleCancelGenerateContentWithAI = () => {
    cancelGenerateAI()

    form.values.description = ' '
    form.values.photos_attributes = []
  }

  const handleBlurDescription = () => {
    const currentValue = form.values.description
    previousValueRef.current = currentValue
  }

  useEffect(() => {
    if (!isLoadingAI.description) {
      if (!form.values.description || form.values.description?.length === 0) {
        form.values.description = oldDescription
      }
    }
  }, [isLoadingAI.all, oldDescription])

  useEffect(() => {
    const first = bundleItems[0]?.category_id
    if (first && !form.values.category_id) {
      form.setFieldValue('category_id', first)
    }
  }, [bundleItems, form])

  useEffect(() => {
    if (!isLoadingAI.description) {
      if (!form.values.description || form.values.description?.length === 0) {
        form.values.description = oldDescription
      }
    }
  }, [isLoadingAI.all, oldDescription])

  return (
    <Box>
      <Text c='dimmed'>{t('new_bundle_properties_intro')}</Text>
      <AlertActionable bg='#f6f6f1'>
        <AlertActionable.Title>
          {t('new_bundle_ai_title')}
          <Badge ml='sm' color='green'>
            {t('new_bundle_ai_badge')}
          </Badge>
        </AlertActionable.Title>
        <AlertActionable.Description mr='xs'>
          {t('new_bundle_ai_description')}
        </AlertActionable.Description>
        <AlertActionable.Actions>
          <Flex gap='sm'>
            {isLoadingAI.all && (
              <Button
                variant='outline'
                onClick={handleCancelGenerateContentWithAI}
                data-testid='cancel-ai'
              >
                {t('new_bundle_ai_cancel')}
              </Button>
            )}
            <Button
              disabled={form.values.bundle_items.length === 0}
              loading={isLoadingAI.all}
              onClick={handleGenerateContentWithAI}
              leftSection={<Icon icon={faWandMagicSparkles} />}
              data-testid='generate-ai'
            >
              {t('new_bundle_ai_generate')}
            </Button>
          </Flex>
        </AlertActionable.Actions>
      </AlertActionable>
      <Flex mt='md' direction='column' gap='md'>
        <Flex direction='column' gap='sm'>
          <Flex gap='xs' align='center'>
            <Text fw='500'>{t('new_bundle_properties_title.title')} </Text>
            <Text c='red.6'>*</Text>
          </Flex>
          <Box style={{ position: 'relative' }}>
            <TextInput
              size='md'
              readOnly={isLoadingAI.all}
              disabled={isLoadingAI.all}
              placeholder={t('new_bundle_properties_title.placeholder')}
              {...form.getInputProps('title')}
              onChange={(event) => {
                form.setFieldValue('title', event.target.value)
              }}
            />
          </Box>
        </Flex>
        <Flex direction='column' gap='sm'>
          <Flex gap='xs' align='center'>
            <Text fw='500'>
              {t('new_bundle_properties_description.title')}{' '}
            </Text>
            <Text c='red.6'>*</Text>
          </Flex>
          <RichTextEditor
            wrapperContainerId={descriptionId}
            placeholder={t('new_bundle_properties_description.placeholder')}
            updateIfChanged
            value={form.values.description || oldDescription}
            error={form.getInputProps('description').error}
            onBlur={handleBlurDescription}
            onChange={handleChangeDescription}
          />
        </Flex>
        <Flex direction='column' gap='sm'>
          <Flex gap='xs' align='center'>
            <Text fw='500'>{t('new_bundle_properties_category.title')} </Text>
            <Text c='red.6'>*</Text>
          </Flex>
          <Select
            value={
              form.values.category_id
                ? String(form.values.category_id)
                : undefined
            }
            data={categories}
            size='md'
            disabled={!bundleItemIds.length}
            onChange={(val) =>
              form.setFieldValue('category_id', val ?? undefined)
            }
          />
        </Flex>
        <Flex direction='column' gap='sm'>
          <Flex gap='xs' align='center'>
            <Text fw='500'>{t('new_bundle_properties_address.title')} </Text>
            <Text c='red.6'>*</Text>
          </Flex>
          <AddressSelect address={bundleAddress} />
          <Text c='dimmed'>{t('new_bundle_properties_address.info')}</Text>
        </Flex>
        <Flex direction='column' w='100%'>
          <Flex gap='xs' align='center' mb='sm'>
            <Text fw='500'>{t('new_bundle_properties_images.title')}</Text>
            <Text c='red.6'>*</Text>
            <Text size='sm' c='red.6'>
              {form.getInputProps('photos_attributes').error}
            </Text>
          </Flex>
          <GridItemPhotosManager
            hasError={Boolean(form.getInputProps('photos_attributes').error)}
            product={form.values as unknown as CoreBundle}
            onChange={(photosAttrs) => {
              form.setFieldValue('photos_attributes', photosAttrs)
            }}
          />
        </Flex>
      </Flex>
    </Box>
  )
}
